// ═══════════════════════════════════════════════════════════════════════════
// SurveyPage — public discovery survey (link it from UserInterviews etc.)
//
// Anonymous; posts to the submit-survey edge function, which writes into the
// interview log. No auth required. Route: /survey
//
// A step-through "carousel": one section per slide with a travel photo banner
// (gradient fallback), progress dots, and Back/Next/Submit.
//
// Styled with FIXED light colours (standard Tailwind palette) so it never
// inherits the admin app's dark theme.
//
// Anti-spam (link is public): a time-trap (reject sub-3s submissions) plus a
// display:none honeypot — neither is autofillable, so real users are never
// dropped.
// ═══════════════════════════════════════════════════════════════════════════

import { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';

type Q = { id: string; text: string; field?: 'last_trip' | 'tools' | 'pain' | 'paid' | 'referral' };

const GROUPS: { title: string; qs: Q[] }[] = [
  { title: 'Your last trip', qs: [
    { id: 'q1', text: 'Tell us about the last trip you took — where, how long, who with?', field: 'last_trip' },
    { id: 'q2', text: 'Walk us through one day of it — what did you actually do, roughly hour by hour?' },
  ] },
  { title: 'In the moment', qs: [
    { id: 'q3', text: 'What did you plan ahead vs. figure out on the spot?' },
    { id: 'q4', text: 'Was there a moment you had unexpected free time or a gap in the day — what did you do?' },
    { id: 'q5', text: 'Ever stood somewhere unsure what to do or where to go next? Tell us about that time.' },
    { id: 'q6', text: 'When you wanted to find something good nearby in the moment, how did you decide?' },
    { id: 'q7', text: 'Anything you only found out later that you’d missed — and wished you’d known while you were there?' },
  ] },
  { title: 'Travelling with others', qs: [
    { id: 'q8', text: 'Think about your last trip with other people — how did you all decide what to do each day?' },
    { id: 'q9', text: 'Where did that planning happen — group chat, a doc, in person? What got messy or lost?' },
    { id: 'q10', text: 'Someone usually becomes the “organiser.” Was that you? What did it cost you in time or stress?' },
  ] },
  { title: 'Content & itineraries', qs: [
    { id: 'q11', text: 'Ever followed someone’s trip online and thought “I want to do exactly that”? What did you do next?' },
    { id: 'q12', text: 'Have you ever tried to get someone’s itinerary — DM’d them, bought a guide, copied a blog? How did it go?' },
  ] },
  { title: 'What you use & what you pay for', qs: [
    { id: 'q13', text: 'Which apps/tools did you use during the trip? Which did you open most?', field: 'tools' },
    { id: 'q14', text: 'What about the trip stressed you out or ate time you hadn’t expected?', field: 'pain' },
    { id: 'q15', text: 'Have you ever paid for anything to help with a trip — a guide, app, planner, tour, agent? What, and why?', field: 'paid' },
    { id: 'q15b', text: 'If you paid for something: roughly how much did it cost — and was it worth it?' },
    { id: 'q16', text: 'Ever had a plan trapped in a PDF, email or doc you wished was just live in an app? What did you do with it?' },
    { id: 'q17', text: 'If you could wave a wand and fix one annoying thing about how you travel, what would it be?' },
  ] },
  { title: 'One more', qs: [
    { id: 'q18', text: 'Who else do you know who travels like you that we should talk to? (a name or @handle is fine)', field: 'referral' },
  ] },
];

const ALL_QS = GROUPS.flatMap(g => g.qs);
const REQUIRED = new Set(['q1', 'q3', 'q6', 'q8', 'q9', 'q11', 'q13', 'q15']);
const qNum = (id: string) => ALL_QS.findIndex(q => q.id === id) + 1;

const AGE_GROUPS = ['18–24', '25–34', '35–44', '45–54', '55–64', '65+'];
const ETHNICITIES = [
  'Asian', 'Black / African', 'Hispanic / Latino', 'Middle Eastern / North African',
  'White / Caucasian', 'Indigenous / Native', 'Mixed', 'Other',
];

// Per-section travel photo (Unsplash CDN) + a gradient fallback if it fails.
const SECTION_IMG = [
  'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?auto=format&fit=crop&w=1200&h=360&q=70',
  'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?auto=format&fit=crop&w=1200&h=360&q=70',
  'https://images.unsplash.com/photo-1526772662000-3f88f10405ff?auto=format&fit=crop&w=1200&h=360&q=70',
  'https://images.unsplash.com/photo-1488646953014-85cb44e25828?auto=format&fit=crop&w=1200&h=360&q=70',
  'https://images.unsplash.com/photo-1502920917128-1aa500764cbd?auto=format&fit=crop&w=1200&h=360&q=70',
  'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1200&h=360&q=70',
];
const SECTION_GRAD = [
  'linear-gradient(135deg,#0B6E58,#0E7490)',
  'linear-gradient(135deg,#0369A1,#0891B2)',
  'linear-gradient(135deg,#B45309,#BE123C)',
  'linear-gradient(135deg,#4338CA,#7C3AED)',
  'linear-gradient(135deg,#0F766E,#155E75)',
  'linear-gradient(135deg,#C2410C,#B45309)',
];

const FIELD =
  'w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 ' +
  'placeholder:text-slate-400 transition-colors focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/25 focus:outline-none';

function SurveyFooter() {
  return (
    <footer className="border-t border-slate-200 bg-white px-6 py-6 text-[11px] text-slate-500">
      <div className="max-w-5xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <a href="/legal/privacy.html?from=survey" className="hover:text-slate-700 transition-colors">Privacy</a>
          <a href="/legal/terms.html?from=survey" className="hover:text-slate-700 transition-colors">Terms</a>
          <a href="/legal/cookies.html?from=survey" className="hover:text-slate-700 transition-colors">Cookies</a>
        </div>
        <p className="text-[10px] text-slate-400">© 2026 DiscoveryGem · Operated by Arrive Tech LLC.</p>
      </div>
    </footer>
  );
}

export function SurveyPage() {
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [name, setName] = useState('');
  const [isCreator, setIsCreator] = useState(false);
  const [ageGroup, setAgeGroup] = useState('');
  const [ethnicity, setEthnicity] = useState('');
  const [hp, setHp] = useState(''); // honeypot (display:none — not autofillable)
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [invalid, setInvalid] = useState<string[]>([]);
  const mountedAt = useRef(Date.now());
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.title = 'DiscoveryGem — Travel survey';
    const m = document.createElement('meta');
    m.name = 'robots'; m.content = 'noindex, nofollow';
    document.head.appendChild(m);
    // Count a visit — once per browser per day (fire-and-forget).
    try {
      const key = 'dg_survey_view_' + new Date().toISOString().slice(0, 10);
      if (!localStorage.getItem(key)) {
        localStorage.setItem(key, '1');
        supabase.functions.invoke('track-survey-view', { body: { referrer: document.referrer } }).catch(() => {});
      }
    } catch (_e) { /* ignore */ }
    return () => { document.head.removeChild(m); };
  }, []);

  const set = (id: string, v: string) => {
    setAnswers(a => ({ ...a, [id]: v }));
    if (v.trim()) setInvalid(prev => prev.filter(x => x !== id));
  };
  const answered = useMemo(() => ALL_QS.filter(q => (answers[q.id] || '').trim()).length, [answers]);
  const last = GROUPS.length - 1;

  // required questions on a given step that are still empty
  const missingIn = (stepIndex: number) =>
    GROUPS[stepIndex].qs.filter(q => REQUIRED.has(q.id) && !(answers[q.id] || '').trim()).map(q => q.id);

  const flagMissing = (miss: string[]) => {
    setInvalid(miss);
    setError(`Please answer the required question${miss.length > 1 ? 's' : ''} (${miss.map(qNum).join(', ')}) before continuing.`);
    cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const go = (dir: 1 | -1) => {
    setError('');
    if (dir === 1) {
      const miss = missingIn(step);
      if (miss.length) { flagMissing(miss); return; }
      setInvalid([]);
    }
    setStep(s => Math.min(last, Math.max(0, s + dir)));
    cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleSubmit = async () => {
    // jump to the first step that still has an unanswered required question
    for (let i = 0; i <= last; i++) {
      const miss = missingIn(i);
      if (miss.length) { setStep(i); flagMissing(miss); return; }
    }
    setError('');
    setSubmitting(true);
    setError('');
    try {
      const notes = ALL_QS.map(q => `Q: ${q.text}\nA: ${(answers[q.id] || '').trim() || '—'}`).join('\n\n');
      const pick = (f: Q['field']) => {
        const q = ALL_QS.find(x => x.field === f);
        return q ? (answers[q.id] || '').trim() || null : null;
      };
      const { data, error: fnErr } = await supabase.functions.invoke('submit-survey', {
        body: {
          name: name.trim() || null,
          persona: isCreator ? 'creator' : 'traveler',
          channel: 'Survey',
          last_trip: pick('last_trip'),
          tools: pick('tools'),
          pain: pick('pain'),
          paid: pick('paid'),
          referral: pick('referral'),
          notes,
          age_group: ageGroup || null,
          ethnicity: ethnicity || null,
          hp,                                   // honeypot (should be empty)
          elapsed: Date.now() - mountedAt.current, // time-trap
        },
      });
      if (fnErr || (data && (data as any).error)) {
        throw new Error((data as any)?.error || fnErr?.message || 'Submission failed');
      }
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e: any) {
      setError(e.message || 'Something went wrong — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Thank-you ──────────────────────────────────────────────────────────────
  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col text-slate-900">
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-xl shadow-slate-200/60 p-10 text-center">
            <img src="/logo.svg" alt="DiscoveryGem" className="w-12 h-12 mx-auto mb-5" />
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Thank you</h1>
            <p className="text-slate-600 leading-relaxed">
              Your answers are in — this genuinely helps us understand how people really travel. Safe travels. ✈️
            </p>
          </div>
        </div>
        <SurveyFooter />
      </div>
    );
  }

  const group = GROUPS[step];

  // ── Survey (carousel) ──────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col text-slate-900">
      <div className="flex-1 w-full max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        {/* Brand + intro */}
        <div className="flex items-center gap-3 mb-6">
          <img src="/logo.svg" alt="DiscoveryGem" className="w-9 h-9" />
          <div>
            <div className="font-semibold text-slate-900 leading-none">DiscoveryGem</div>
            <div className="text-[10px] text-slate-500 uppercase tracking-widest mt-0.5">Traveller research</div>
          </div>
        </div>
        {step === 0 && (
          <p className="text-sm text-slate-500 mb-5 leading-relaxed">
            A few questions about your <strong className="text-slate-700 font-semibold">last real trip</strong> — no right answers,
            nothing being sold. Stories beat opinions, so tell us what actually happened. <span className="whitespace-nowrap">~5–10 min.</span>
          </p>
        )}

        {error && (
          <div className="mb-5 px-4 py-3 rounded-xl text-sm bg-red-50 border border-red-200 text-red-700">{error}</div>
        )}

        {/* Honeypot — display:none so browser autofill leaves it alone */}
        <div style={{ display: 'none' }} aria-hidden="true">
          <label>Company website</label>
          <input type="text" name="company_website" tabIndex={-1} autoComplete="off"
            value={hp} onChange={e => setHp(e.target.value)} />
        </div>

        {/* Carousel card */}
        <div ref={cardRef} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          {/* Photo banner */}
          <div className="relative h-40 sm:h-48" style={{ background: SECTION_GRAD[step] }}>
            <img
              key={step} src={SECTION_IMG[step]} alt="" loading="lazy"
              className="absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity duration-700"
              onLoad={e => { (e.currentTarget as HTMLImageElement).style.opacity = '1'; }}
              onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/20 to-transparent" />
            <div className="absolute bottom-0 left-0 p-5 text-white">
              <div className="text-[11px] uppercase tracking-[0.18em] text-white/80 mb-1">Step {step + 1} of {GROUPS.length}</div>
              <h2 className="text-xl sm:text-2xl font-bold drop-shadow-sm">{group.title}</h2>
            </div>
          </div>

          {/* Body */}
          <div className="p-6 space-y-5">
            {step === 0 && (
              <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Your name or email <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <input type="text" value={name} onChange={e => setName(e.target.value)}
                  placeholder="So we can follow up — or leave blank to stay anonymous" className={FIELD} />
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Age group <span className="text-slate-400 font-normal">(optional)</span></label>
                    <select value={ageGroup} onChange={e => setAgeGroup(e.target.value)} className={FIELD}>
                      <option value="">Prefer not to say</option>
                      {AGE_GROUPS.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Ethnicity <span className="text-slate-400 font-normal">(optional)</span></label>
                    <select value={ethnicity} onChange={e => setEthnicity(e.target.value)} className={FIELD}>
                      <option value="">Prefer not to say</option>
                      {ETHNICITIES.map(x => <option key={x} value={x}>{x}</option>)}
                    </select>
                  </div>
                </div>
                <label className="flex items-center gap-2.5 mt-3 text-sm text-slate-700 cursor-pointer select-none">
                  <input type="checkbox" checked={isCreator} onChange={e => setIsCreator(e.target.checked)}
                    className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                  I create travel content (blog, Instagram, YouTube, TikTok…)
                </label>
              </div>
            )}

            {group.qs.map(q => {
              const num = ALL_QS.indexOf(q) + 1;
              const required = REQUIRED.has(q.id);
              const isBad = invalid.includes(q.id);
              return (
                <div key={q.id}>
                  <label className="block text-sm text-slate-800 mb-2 leading-snug">
                    <span className="text-slate-400 mr-1.5 tabular-nums">{num}.</span>{q.text}
                    {required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  <textarea value={answers[q.id] || ''} onChange={e => set(q.id, e.target.value)}
                    rows={2} className={`${FIELD} resize-y`}
                    style={isBad ? { borderColor: '#ef4444', boxShadow: '0 0 0 2px #ef4444' } : undefined} />
                </div>
              );
            })}
          </div>

          {/* Nav */}
          <div className="flex items-center justify-between gap-4 px-6 py-4 border-t border-slate-200 bg-slate-50/60">
            <button
              onClick={() => go(-1)} disabled={step === 0}
              className="px-4 py-2 rounded-xl text-sm font-medium text-slate-600 hover:text-slate-900 disabled:opacity-0 disabled:cursor-default transition-colors"
            >
              ← Back
            </button>

            {/* Progress dots */}
            <div className="flex items-center gap-1.5">
              {GROUPS.map((_, i) => (
                <span key={i} className={`h-1.5 rounded-full transition-all ${i === step ? 'w-6 bg-emerald-600' : 'w-1.5 bg-slate-300'}`} />
              ))}
            </div>

            {step < last ? (
              <button onClick={() => go(1)}
                className="px-6 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition-colors shadow-sm">
                Next →
              </button>
            ) : (
              <button onClick={handleSubmit} disabled={submitting}
                className="px-6 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm">
                {submitting ? 'Submitting…' : 'Submit'}
              </button>
            )}
          </div>
        </div>

        <p className="text-xs text-slate-400 text-center mt-4">
          {answered} of {ALL_QS.length} answered · your answers are private and used only for our research.
        </p>
      </div>
      <SurveyFooter />
    </div>
  );
}
